import {
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
} from 'firebase/auth';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { auth } from '../firebase';
import { getFarmerByOwnerUid, getOperatorRole, getUserProfile } from '../lib/firestore';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [operatorRole, setOperatorRole] = useState(null);
  const [isFarmer, setIsFarmer] = useState(false);
  const [farmerProfile, setFarmerProfile] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser);
      if (nextUser) {
        const [role, ownedFarmer, profile] = await Promise.all([
          getOperatorRole(nextUser.uid),
          getFarmerByOwnerUid(nextUser.uid),
          getUserProfile(nextUser.uid),
        ]);
        setOperatorRole(role);
        setIsFarmer(Boolean(ownedFarmer));
        setFarmerProfile(ownedFarmer);
        setUserProfile(profile);
      } else {
        setOperatorRole(null);
        setIsFarmer(false);
        setFarmerProfile(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsub;
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      operatorRole,
      userProfile,
      isOperator: Boolean(operatorRole),
      isAdmin: operatorRole === 'site_admin',
      isStaff: operatorRole === 'staff',
      isDoctor: userProfile?.role === 'doctor',
      isFarmerRole: userProfile?.role === 'farmer' || isFarmer,
      isFarmer,
      farmerProfile,
      canAccessAdminConsole: Boolean(operatorRole) || userProfile?.role === 'doctor',
      canManageOperatorPosts: operatorRole === 'site_admin' || userProfile?.role === 'doctor',
      canManageFarmerReports: operatorRole === 'site_admin' || userProfile?.role === 'farmer' || isFarmer,
      canReviewArticles: operatorRole === 'site_admin' || userProfile?.role === 'doctor',
      displayName:
        userProfile?.name
        || farmerProfile?.name
        || user?.email?.split('@')[0]
        || 'ユーザー',
      roleLabel:
        operatorRole === 'site_admin'
          ? 'サイト管理者'
          : operatorRole === 'staff'
            ? '運営'
            : userProfile?.role === 'doctor'
              ? 'ドクター'
              : userProfile?.role === 'farmer' || isFarmer
                ? '農家'
              : isFarmer
                ? '農家'
                : 'ユーザー',
      signInWithEmail: (email, password) => signInWithEmailAndPassword(auth, email, password),
      changePassword: async (currentPassword, newPassword) => {
        if (!auth.currentUser?.email) throw new Error('no-user');
        const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
        await reauthenticateWithCredential(auth.currentUser, credential);
        await updatePassword(auth.currentUser, newPassword);
      },
      signOut: () => signOut(auth),
    }),
    [farmerProfile, isFarmer, loading, operatorRole, user, userProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}
